import { FC, useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import dayjs from "dayjs";

import { TvMatchRef } from "../Lib";
import { searchTvMatches } from "../Providers/tv-api";
import { useDebouncedSearch } from "./useDebouncedSearch";

export const matchLabel = (m: TvMatchRef) => {
  // У несыгранных матчей в базе счёт 0:0 — показываем его только для прошедших.
  const played = m.date != null && dayjs(m.date).isBefore(dayjs());
  const score =
    played && m.homeScore != null && m.guestScore != null
      ? ` ${m.homeScore}:${m.guestScore}`
      : "";
  const date = m.date ? dayjs(m.date).format("DD.MM.YYYY HH:mm") : "без даты";
  return `${m.home} — ${m.guest}${score} · ${m.tour ?? ""} · ${date} · №${
    m.matchId
  }`;
};

type Props = {
  leagueId?: number | null;
  value?: number | null;
  onChange?: (value: number | null, match?: TvMatchRef) => void;
  /** Уже выбранный матч: чтобы показать его название до поиска. */
  initial?: TvMatchRef | null;
  disabled?: boolean;
};

/** Выбор матча лиги: поиск по клубу или номеру, ближайшие к сегодня — сверху. */
export const MatchPicker: FC<Props> = ({
  leagueId,
  value,
  onChange,
  initial,
  disabled,
}) => {
  const { items, loading, error, setText } = useDebouncedSearch(
    (text) =>
      leagueId ? searchTvMatches(leagueId, text) : Promise.resolve([]),
    String(leagueId ?? "")
  );

  // Выбран матч, про который известен только номер (например, в слайде), — подтягиваем название.
  const [known, setKnown] = useState<TvMatchRef | null>(initial ?? null);
  useEffect(() => {
    if (initial) {
      setKnown(initial);
    } else if (value && leagueId && known?.matchId !== value) {
      searchTvMatches(leagueId, String(value))
        .then((found) =>
          setKnown(found.find((m) => m.matchId === value) ?? null)
        )
        .catch(() => setKnown(null));
    }
  }, [value, leagueId, initial, known?.matchId]);

  const options = useMemo(() => {
    const list = [...items];
    if (
      known &&
      known.matchId === value &&
      !list.some((m) => m.matchId === known.matchId)
    ) {
      list.unshift(known);
    }
    return list.map((m) => ({
      value: m.matchId,
      label: matchLabel(m),
      match: m,
    }));
  }, [items, known, value]);

  return (
    <Select
      showSearch
      allowClear
      style={{ width: "100%" }}
      value={value ?? undefined}
      disabled={disabled || !leagueId}
      placeholder={leagueId ? "Клуб или номер матча" : "Сначала выберите лигу"}
      filterOption={false}
      onSearch={setText}
      loading={loading}
      notFoundContent={error ?? (loading ? "Ищем…" : "Матчи не найдены")}
      options={options}
      onChange={(v, option) =>
        onChange?.(
          (v as number) ?? null,
          (option as { match?: TvMatchRef } | undefined)?.match
        )
      }
    />
  );
};
